import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Download, Loader2, UploadCloud, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-xl border p-3 text-center ${tone === "bad" && value > 0 ? "border-rose-300 bg-rose-50 dark:bg-rose-900/20" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"}`}>
    <div className="text-xl font-bold">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
  </div>
);

export const SatsExportDialog = ({ open, onOpenChange, classroom, section, examKey }) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/sats-export/templates", { params: { class_name: classroom } }).then(({ data }) => {
      setTemplates(data.templates || []);
      const best = (data.templates || []).find((t) =>
        String(t.exam_type || "").toUpperCase() === String(examKey).toUpperCase()) || (data.templates || [])[0];
      setTemplateId(best ? best.id : "");
    }).catch(() => {});
  }, [open, classroom, examKey]);

  const deleteTemplate = async () => {
    if (!templateId) return;
    if (!window.confirm("Delete this SATS template? Exports using it will no longer be possible until re-uploaded.")) return;
    try {
      await api.delete(`/sats-export/template/${templateId}`);
      toast.success("Template deleted.");
      setTemplates((t) => t.filter((x) => x.id !== templateId));
      setTemplateId("");
      setReport(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const [assigning, setAssigning] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const assignStudent = async (satsId, studentId) => {
    if (!studentId) return;
    setAssigning(satsId);
    try {
      await api.post("/sats-export/assign", { sats_id: satsId, student_id: studentId });
      toast.success("Student assigned — enrolment number saved.");
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAssigning(""); }
  };

  useEffect(() => {
    if (!open || !templateId) { setReport(null); return; }
    setLoading(true);
    api.get("/sats-export/preview", { params: { template_id: templateId, class_name: classroom, section, exam_key: examKey } })
      .then(({ data }) => setReport(data))
      .catch((e) => { setReport(null); toast.error(apiErr(e)); })
      .finally(() => setLoading(false));
  }, [open, templateId, classroom, section, examKey, reloadKey]);

  const uploadTemplate = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/sats-export/template", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SATS template uploaded.");
      setTemplates((t) => [data.template, ...t]);
      setTemplateId(data.template.id);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/sats-export/export", {
        params: { template_id: templateId, class_name: classroom, section, exam_key: examKey },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SATS_${examKey}_Class${classroom}_${section}.xls`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("SATS Excel generated — template structure preserved.");
    } catch (e) {
      let msg = "Export failed.";
      try { msg = JSON.parse(await e.response.data.text()).detail; } catch { msg = apiErr(e); }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const s = report?.summary;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sats-export-dialog">
        <DialogHeader>
          <DialogTitle>Karnataka SATS Result Export</DialogTitle>
          <DialogDescription>
            Class {classroom} · ALL sections · {examKey} — grades are written into the original SATS
            .xls template (matched by SATS Enrollment No = Student ID across every section of Std {classroom}).
            Everything else is preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">SATS Master Template</div>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="sats-template-select"><SelectValue placeholder="Select SATS template" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.exam_type || "?"} · Std {t.standard || "?"} · {t.filename} ({t.student_count} students)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept=".xls" className="hidden" onChange={(e) => uploadTemplate(e.target.files?.[0])} data-testid="sats-template-file" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sats-template-upload">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1" />} Upload SATS .xls
          </Button>
          {templateId && (
            <Button variant="outline" onClick={deleteTemplate} className="border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" title="Delete selected template" data-testid="sats-template-delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {loading && <div className="py-8 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Validating…</div>}

        {report && !loading && (
          <div className="space-y-4">
            {report.exam_mismatch && (
              <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm px-3 py-2" data-testid="sats-exam-mismatch">
                <AlertTriangle className="h-4 w-4 shrink-0" /> Template is for {report.template.exam_type}, but you selected {examKey}. Grades exported will be {examKey} grades.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="sats-export-summary">
              <Stat label="In SATS file" value={s.students_in_sats} />
              <Stat label="Matched" value={s.matched} />
              <Stat label="Ready" value={s.ready} />
              <Stat label="Missing grades" value={s.missing_grades} tone="bad" />
              <Stat label="Unmatched" value={s.unmatched} tone="bad" />
            </div>
            {s.ready === s.students_in_sats ? (
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-testid="sats-ready-banner">
                <CheckCircle2 className="h-4 w-4" /> All {s.ready} students ready for SATS upload.
              </div>
            ) : null}
            {report.issues.length > 0 && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-slate-50 dark:bg-slate-800 text-slate-500">
                  Needs attention ({report.issues.length}) — these cells are left blank / rows untouched
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                  {report.issues.slice(0, 100).map((i, k) => (
                    <div key={k} className="px-3 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-mono text-xs text-slate-500">{i.sats_id}</span>
                      <span className="font-medium">{i.student}</span>
                      <span className="text-slate-500">{i.subject}</span>
                      <span className="text-rose-600 ml-auto">{i.issue}</span>
                      {i.issue.startsWith("Not matched") && (
                        <select
                          className="w-full sm:w-auto text-xs rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1"
                          disabled={assigning === i.sats_id}
                          defaultValue={i.suggestions?.[0]?.student_id || ""}
                          onChange={(e) => assignStudent(i.sats_id, e.target.value)}
                          data-testid={`sats-assign-${i.sats_id}`}
                        >
                          <option value="">Assign student…</option>
                          {(i.suggestions || []).map((sg) => (
                            <option key={`s-${sg.student_id}`} value={sg.student_id}>★ {sg.name} ({sg.section})</option>
                          ))}
                          {(report.missing_in_template || [])
                            .filter((m) => !(i.suggestions || []).some((sg) => sg.student_id === m.student_id))
                            .map((m) => (
                              <option key={m.student_id} value={m.student_id}>{m.name} ({m.section})</option>
                            ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {report.missing_in_template.length > 0 && (
              <div className="text-xs text-slate-500" data-testid="sats-missing-in-template">
                {report.missing_in_template.length} app student(s) not present in the SATS file:{" "}
                {report.missing_in_template.slice(0, 8).map((m) => m.name).join(", ")}
                {report.missing_in_template.length > 8 ? "…" : ""} (SATS student list may need updating)
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={download} disabled={downloading || s.matched === 0 || s.duplicates.length > 0}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="sats-download-btn">
                {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Download SATS Excel
              </Button>
            </div>
          </div>
        )}
        {!templateId && !loading && (
          <div className="py-6 text-center text-sm text-slate-500">
            Upload the .xls file downloaded from the Karnataka SATS portal to begin.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SatsExportDialog;
